"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
const getCategory = (post: any) => {
  if (post.categories && post.categories.length > 0) {
    return post.categories[0].name;
  }
  return "Home Organization";
};
function PostsGridConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div data-section-id="19462" className="grid grid-cols-1 lg:grid-cols-3 gap-8 h-full">
        <div className="lg:col-span-2 h-[600px] bg-muted animate-pulse rounded-none" />
        <div className="flex flex-col gap-8 h-full">
          <div className="flex-1 bg-muted animate-pulse rounded-none" />
          <div className="flex-1 bg-muted animate-pulse rounded-none" />
        </div>
      </div>;
  }
  if (error) {
    return <div className="py-20 text-center">
        <p className="text-muted-foreground mb-4 font-sans">Unable to load the latest journal entries.</p>
        <Button variant="link" onClick={() => refetch()} className="text-accent-foreground underline underline-offset-4 hover:text-accent-foreground/80 font-sans">
          Try Again
        </Button>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="py-20 text-center">
        <p className="text-muted-foreground font-sans">No articles found in the collection.</p>
      </div>;
  }
  const featuredPost = posts[0];
  const sidePosts = posts.slice(1, 3);
  return <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-10 lg:gap-x-10">
      {}
      <div className="lg:col-span-8 group" data-index={0}>
        <Link to={featuredPost.link || "#"} className="block space-y-6">
          <div className="overflow-hidden bg-muted">
            <AspectRatio ratio={16 / 10}>
              {featuredPost.featuredImage ? <img src={featuredPost.featuredImage} alt={featuredPost.title} className="h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-accent/30 text-muted-foreground">
                  <span className="font-serif italic">The Organized Space</span>
                </div>}
            </AspectRatio>
          </div>
          
          <div className="space-y-3 max-w-3xl">
            <Badge variant="secondary" className="rounded-none border-none bg-transparent px-0 text-[10px] uppercase tracking-[0.2em] font-serif text-accent-foreground hover:bg-transparent shadow-none">
              {getCategory(featuredPost)}
            </Badge>
            <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.15] text-foreground group-hover:text-muted-foreground transition-colors duration-500">
              {featuredPost.title}
            </h3>
          </div>
        </Link>
      </div>

      {}
      <div className="lg:col-span-4 flex flex-col gap-10 lg:gap-8">
        {sidePosts.map((post, i) => <div key={post.id} className="group flex flex-col h-full" data-index={i + 1}>
            <Link to={post.link || "#"} className="block space-y-4 h-full">
              <div className="overflow-hidden bg-muted">
                <AspectRatio ratio={3 / 2}>
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-accent/30 text-muted-foreground">
                      <span className="font-serif italic">Journal Post</span>
                    </div>}
                </AspectRatio>
              </div>
              
              <div className="space-y-2">
                <Badge variant="secondary" className="rounded-none border-none bg-transparent px-0 text-[10px] uppercase tracking-[0.2em] font-serif text-accent-foreground hover:bg-transparent shadow-none">
                  {getCategory(post)}
                </Badge>
                <h4 className="font-serif text-xl md:text-2xl leading-tight text-foreground group-hover:text-muted-foreground transition-colors duration-500">
                  {post.title}
                </h4>
              </div>
            </Link>
          </div>)}
      </div>
    </div>;
}
export default function LatestFromTheJournal() {
  const wp_query_params = {
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="the-latest-from-the-journal" className="bg-background py-20 lg:py-32">
      <div className="container mx-auto px-4 md:px-8">
        <div className="mb-12 md:mb-20 flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-border/50 pb-8">
          <div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground tracking-tight">
              The Journal
            </h2>
            <p className="mt-4 font-sans text-muted-foreground max-w-md text-lg leading-relaxed">
              Simple organization ideas, decluttering guides, and smart storage solutions for everyday living.
            </p>
          </div>
          <Link to="/JournalPage" className="hidden md:inline-flex font-sans text-xs uppercase tracking-[0.2em] text-foreground hover:text-accent-foreground transition-colors border-b border-transparent hover:border-accent-foreground pb-1">
            Explore the Journal
          </Link>
        </div>

        <PostsProvider wp_query={{
        per_page: 3,
        orderby: "date",
        order: "desc"
      }}>
          <PostsGridConsumer />
        </PostsProvider>

        <div className="mt-16 md:hidden text-center">
          <Link to="/JournalPage" className="inline-flex font-sans text-xs uppercase tracking-[0.2em] text-foreground hover:text-accent-foreground transition-colors border-b border-foreground hover:border-accent-foreground pb-1">
            Explore the Journal
          </Link>
        </div>
      </div>
    </section>;
}